import type { CSSProperties } from 'react';

interface WordmarkProps {
  height?: number;
  /** Any CSS colour — the inlined vector is tinted to this via `currentColor`. */
  color?: string;
  style?: CSSProperties | undefined;
  title?: string;
}

/** Real wordmark aspect ratio from the Figma source (720.995 × 113.35). */
const WORDMARK_RATIO = 720.995 / 113.35;

/**
 * Locked NORVIX letterform paths, exported 1:1 from the Figma brand source
 * (Intel One Display). Inlined as an SVG (rather than a CSS mask over an
 * external asset) so the wordmark always renders the exact letterforms,
 * tinted to `color` — no mask-support / asset-path / cross-origin fragility.
 */
const WORDMARK_PATHS: readonly string[] = [
  'M13.0074 21.8339V110.873H0V2.4776H14.2462L81.9157 91.0519V2.4776H94.9231V110.873H80.9866L13.0074 21.8339Z',
  'M138.616 56.83C138.616 40.8288 143.933 27.3568 154.566 16.4141C165.199 5.47137 178.309 0 193.898 0C209.589 0 222.648 5.41975 233.075 16.2593C243.605 26.9955 248.87 40.4159 248.87 56.5203C248.87 72.6247 243.553 86.1483 232.92 97.091C222.39 107.931 209.331 113.35 193.743 113.35C178.051 113.35 164.941 107.982 154.411 97.2459C143.881 86.4064 138.616 72.9344 138.616 56.83ZM152.243 56.5203C152.243 69.218 156.218 79.9026 164.167 88.5743C172.219 97.1426 182.129 101.427 193.898 101.427C205.666 101.427 215.474 97.1942 223.319 88.7291C231.268 80.1607 235.243 69.5277 235.243 56.83C235.243 44.1323 231.268 33.4992 223.319 24.9309C215.37 16.2593 205.511 11.9235 193.743 11.9235C181.871 11.9235 171.961 16.2076 164.012 24.776C156.166 33.2411 152.243 43.8226 152.243 56.5203Z',
  'M350.627 65.1919L381.442 110.873H365.647L336.38 66.7404H305.875V110.873H292.558V2.4776H340.252C352.433 2.4776 361.931 5.31652 368.744 10.9944C375.557 16.569 378.964 24.2598 378.964 34.067C378.964 42.1192 376.487 48.881 371.531 54.3524C366.576 59.8238 359.608 63.4369 350.627 65.1919ZM365.492 34.3767C365.492 28.0795 363.221 23.1759 358.679 19.666C354.24 16.0528 347.994 14.2462 339.942 14.2462H305.875V55.1266H338.858C347.013 55.1266 353.466 53.2684 358.214 49.552C363.066 45.7324 365.492 40.674 365.492 34.3767Z',
  'M460.793 110.873L412.789 2.4776H427.345L467.761 93.8392L507.712 2.4776H521.959L473.955 110.873H460.793Z',
  'M563.214 110.873V2.4776H576.531V110.873H563.214Z',
  'M634.053 110.873H617.949L660.997 54.9718L620.271 2.4776H636.995L669.204 45.6808L701.413 2.4776H717.517L677.411 54.8169L720.614 110.873H704.045L669.049 64.2628L634.053 110.873Z',
];

/** NORVIX wordmark — the locked vector logotype, tinted to `color`. */
export function Wordmark({
  height = 28,
  color = 'var(--text-primary)',
  style,
  title = 'NORVIX',
}: WordmarkProps): JSX.Element {
  return (
    <svg
      role="img"
      aria-label={title}
      viewBox="0 0 720.995 113.35"
      height={height}
      width={height * WORDMARK_RATIO}
      style={{ display: 'block', color, ...style }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      {WORDMARK_PATHS.map((d) => (
        <path key={d.slice(0, 16)} d={d} fill="currentColor" />
      ))}
    </svg>
  );
}
